/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh } from '@fortawesome/free-solid-svg-icons/faRefresh'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import ManagementReport from './ManagementReport'
import { useQuery } from '@tanstack/react-query'
import { fetchLease } from '../../lib/rest/leaseAPIClient'
import {
  downloadManagementReport,
  fetchManagementReportData,
  fetchManagementReportYearsAvailable
} from '../../lib/rest/managementReportAPIClient'
import { useMemo } from 'react'
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { FormattedMessage, useIntl } from 'react-intl'
import { Box, Button, Card, Divider, Group, Select, Stack } from '@mantine/core'
import Toolbar from '../../../../client/js/components/Toolbar'
import RequirePerm from '../../../auth/components/RequirePerm'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { getTransactionCreateLink } from '../../lib/links/transactionLinks'
import { faMinusCircle } from '@fortawesome/free-solid-svg-icons/faMinusCircle'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle'

export type ManagementReportListCardProps = {
  leaseId: number,
  year: number,
  onYearChange (year: number): void
}

function ManagementReportListCard (props: ManagementReportListCardProps) {
  const { leaseId, onYearChange, year } = props
  const intl = useIntl()

  const leaseQuery = useQuery({
    enabled: leaseId > 0,
    queryKey: ['lease', leaseId],
    queryFn: () => fetchLease(leaseId),
    refetchOnWindowFocus: false
  })
  const { data: lease } = leaseQuery
  const { estateId, lessorId: customerId } = lease || {}

  const reportQuery = useQuery({
    enabled: customerId > 0 && estateId > 0 && year > 0,
    queryKey: ['managementReport', customerId, estateId, year],
    queryFn: () =>
      fetchManagementReportData({
        customerId,
        estateId,
        year,
        isManagementReport: true
      }),
    refetchOnWindowFocus: false
  })

  const yearsQuery = useQuery({
    enabled: leaseId > 0,
    queryKey: ['leaseManagementReportYears', leaseId],
    queryFn: () => fetchManagementReportYearsAvailable({ customerId, leaseId }),
    refetchOnWindowFocus: false
  })
  const years = useMemo<number[]>(
    () => (yearsQuery?.data?.data || []),
    [yearsQuery?.data?.data]
  )

  const reportFileQuery = useQuery({
    enabled: false,
    queryKey: ['downloadManagementReport', customerId, estateId, year],
    queryFn: () => downloadManagementReport({ customerId, estateId, year })
  })

  const transactionParams = useMemo(() => ({
    customerId: lease?.lessorId,
    estateId: lease?.estateId,
    leaseId: lease?.id
  }), [lease?.estateId, lease?.id, lease?.lessorId])

  return (
    <Card>
      <Stack>
        <Toolbar>
          <Select
            data={years.map(String)}
            onChange={(value) => onYearChange(Number(value))}
            value={year != null ? String(year) : null}
          />

          <Button
            disabled={reportQuery.isRefetching || yearsQuery.isRefetching}
            loading={reportQuery.isRefetching || yearsQuery.isRefetching}
            onClick={() => {
              yearsQuery.refetch()
              reportQuery.refetch()
            }}
          >
            <FontAwesomeIcon icon={faRefresh} />
          </Button>

          {year != null
            ? <Button
              disabled={reportFileQuery.isFetching}
              loading={reportFileQuery.isFetching}
              onClick={() => {
                reportFileQuery.refetch()
              }}
              leftSection={<FontAwesomeIcon fixedWidth icon={faDownload} />}
            >
              {intl.formatMessage({
                id: 'download',
                defaultMessage: 'Télécharger'
              })}
            </Button>
            : null}

          <RequirePerm
            permissions={[
              'write:invoice',
              'write:transaction'
            ]}
            render={() => (
              <Group gap="xs" ms="auto">
                <Button
                  size="sm"
                  component={AnchorLink}
                  to={`${getTransactionCreateLink(true, transactionParams)}`}
                >
                  <FontAwesomeIcon icon={faMinusCircle} />
                  <Box visibleFrom="lg" ml="xs">
                    <FormattedMessage
                      id="expense"
                      defaultMessage="Dépense"
                    />
                  </Box>
                </Button>
                <Button
                  size="sm"
                  component={AnchorLink}
                  to={`${getTransactionCreateLink(false, transactionParams)}`}
                >
                  <FontAwesomeIcon icon={faPlusCircle} />
                  <Box visibleFrom="lg" ml="xs">
                    <FormattedMessage
                      id="revenue"
                      defaultMessage="Revenu"
                    />
                  </Box>
                </Button>
              </Group>
            )}
          />
        </Toolbar>

        <Divider />

        <LoadingHelper
          query={reportQuery}
          render={() => <ManagementReport
            data={reportQuery.data}
            isManagementReport
            onRefresh={reportQuery.refetch}
          />}
        />
      </Stack>
    </Card>
  )
}

export default ManagementReportListCard
